import { combineReducers, configureStore } from '@reduxjs/toolkit';

import { defaultServerUrls, environment, getEnvironmentOverride } from '@paragon/environment-tools';
import { logInData } from '@paragon/login-tools';
import { notifications } from '@paragon/notification-tools';
import { SyncClient } from '@paragon/sync-client/index';
import { spinnerMessages } from '@paragon/task-spinner';
import { theme } from '@paragon/themes';

import { jobLockingMiddleware } from '../modules/jobLocking';
import { SYNCHRONIZED_ACTIONS } from '../syncConfiguration';
import { annotations } from './annotations';
import { annotationTemplates } from './annotationTemplates';
import { documents } from './documents';
import { documentTools } from './documentTools';
import { job } from './job';
import { popup } from './popup';
import { seals } from './seals';
import { users } from './users';
import { view } from './view';

const rootReducer = combineReducers({
  logInData,
  users,
  job,
  documents,
  annotations,
  popup,
  documentTools,
  annotationTemplates,
  view,
  seals,
  spinnerMessages,
  environment,
  notifications,
  theme,
});

const params = new URLSearchParams(window.location.search);

// Load job from URL parameter if one specified
const jobIdString = params.get('jobId');
if (jobIdString === null) {
  throw new Error('Job ID not given');
}

const currentEnvironment = getEnvironmentOverride() || process.env.APP_ENV;

const shouldSync = params.get('sync') === 'true';

let syncClient: SyncClient | null = null;

if (shouldSync) {
  syncClient = new SyncClient({
    serverUrl: defaultServerUrls[currentEnvironment!].synchronizationServerUrl,
  });

  syncClient.on('connect', () => {
    syncClient!.state.configurations = [
      {
        room: `#pdfEditor.job.${jobIdString}`,
        synchronizedActions: SYNCHRONIZED_ACTIONS,
      },
    ];
  });
}

const middlewareToApply = [jobLockingMiddleware];
if (syncClient) {
  middlewareToApply.push(syncClient.state.middleware);
}

export const store = configureStore({
  reducer: rootReducer,
  middleware: (getDefaultMiddleware) => getDefaultMiddleware().concat(middlewareToApply),
});

if (syncClient) {
  syncClient.state.store = store;
}

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
